import { Component, computed, effect, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Select } from "@ui";
import type { Page } from "../../data-grid/grid-data.provider";
import type { HeaderRendererColumn } from "../../data-grid/header-renderer.interface";
import type { Corpus } from "@pienso/ingest-types/corpus.interface";
import { columnTypeDisplayNames, ES_TYPE } from "../../services/elastic-search.service";
import { ColumnTypeService } from "./column-type.service";
import { type DateFormat, DateFormatComponent } from "./date-format.component";

interface EsTypeOption {
  type: ES_TYPE;
  display: string;
}

@Component({
  template: `
    <div
      class="column-type-header-cell p-flex-col-xxs p-flex-start p-w-full"
      [class.modified]="service.isSet(column().prop)"
    >
      <span class="datatable-header-cell-label p-block p-w-full">{{ column().name || "\u00a0" }}</span>
      <p-select
        class="p-w-full"
        trackBy="type"
        placeholder="Select type"
        [options]="ES_TYPE_OPTIONS"
        [ngModel]="selectedOption"
        (ngModelChange)="columnTypeChanged($event)"
      />
      @if (isDate()) {
        <app-date-format
          class="p-block p-w-full"
          [initialValue]="format"
          [samples]="samples()"
          (valueChanged)="onDateFormatChange($event)"
        />
      }
    </div>
  `,
  styleUrls: ["./column-type-header.component.scss"],
  imports: [FormsModule, Select, DateFormatComponent],
})
export class ColumnTypeHeaderComponent {
  service = inject(ColumnTypeService);

  readonly corpus = input.required<Corpus>();
  readonly column = input.required<HeaderRendererColumn>();
  readonly editable = input(true);
  readonly page = input.required<Page>();

  readonly samples = computed(() => this.page().rows.map((row) => row[this.column().prop]));

  private readonly columnTypes = toSignal(this.service.columnTypes$);

  selectedOption: EsTypeOption | null = null;
  format: string | undefined;

  readonly ES_TYPE_OPTIONS: EsTypeOption[] = [
    { type: ES_TYPE.TEXT, display: columnTypeDisplayNames[ES_TYPE.TEXT] },
    { type: ES_TYPE.KEYWORD, display: columnTypeDisplayNames[ES_TYPE.KEYWORD] },
    { type: ES_TYPE.LONG, display: columnTypeDisplayNames[ES_TYPE.LONG] },
    { type: ES_TYPE.DOUBLE, display: columnTypeDisplayNames[ES_TYPE.DOUBLE] },
    { type: ES_TYPE.DATE, display: columnTypeDisplayNames[ES_TYPE.DATE] },
    { type: ES_TYPE.BOOLEAN, display: columnTypeDisplayNames[ES_TYPE.BOOLEAN] },
  ];

  constructor() {
    // effect() waits for the required inputs, unlike a direct columnTypes$ subscription, which
    // emits synchronously in the constructor before `column` is set.
    effect(() => {
      this.column();
      this.columnTypes();
      this.updateType();
    });
  }

  columnTypeChanged(option: EsTypeOption | null): void {
    if (!option || option.type === this.selectedOption?.type) {
      return;
    }

    this.selectedOption = option;
    if (option.type === ES_TYPE.DATE) {
      this.format = this.formatForColumn();
      this.service.setType(this.column().prop, option.type, this.format);
    } else {
      this.format = undefined;
      this.service.setType(this.column().prop, option.type);
    }
  }

  onDateFormatChange(format: DateFormat | null): void {
    this.format = format?.value;
    this.service.setType(this.column().prop, ES_TYPE.DATE, this.format);
  }

  isDate(): boolean {
    return this.selectedOption?.type === ES_TYPE.DATE;
  }

  private updateType(): void {
    const column = this.column();
    const dataType = this.service.columnTypes?.[column.prop] ?? column.dataType;
    this.selectedOption = this.ES_TYPE_OPTIONS.find((option) => option.type === dataType?.type) ?? null;
    this.format = dataType?.format;
  }

  private formatForColumn(): string | undefined {
    const column = this.column();
    return this.service.columnTypes?.[column.prop]?.format ?? column.dataType?.format;
  }
}
